import moment from "moment";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { useQueryParamsHook } from "@/hooks/useQueryParamsHook";
import { LinkItemsApi } from "@/apis/LinkItemsApi";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { useDeletePostMutation, useMyPostsQuery, usePostQuery } from "@/hooks/ReactQuery/PostReactQuery/PostReactQuery";
import { FgPostEditor } from "./fragments/FgPostEditor";

const ICON_BUTTON = "rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100";

/**
 * 글 탭: 내 글 목록 ↔ 글 쓰기 (?postId=new | 글 ID).
 * 공개 페이지에는 페이지 관리 → 모듈 추가 → "글 목록" 모듈로 보여 준다.
 */
export function FdPosts() {
    const userPage = useUserPageContextValueHook()?.userPage;
    const queryParamsHook = useQueryParamsHook();
    const postId = queryParamsHook?.params?.postId;

    const postsQuery = useMyPostsQuery({ userPageId: userPage?.id });
    const postQuery = usePostQuery({ id: postId && postId !== 'new' ? postId : null });
    const deletePostMutation = useDeletePostMutation();

    const pages = postsQuery?.data?.pages;
    const posts = pages?.flatMap(page => page.items) || [];
    const total = pages?.[0]?.total || 0;
    const groups = pages?.[0]?.groups || [];

    const setPostId = (id) => {
        const params = { ...queryParamsHook?.params };
        if (id) {
            params.postId = id;
        } else {
            delete params.postId;
        }
        queryParamsHook.setSearchParams(params, { replace: !id });
    }

    const handleSaved = (saved, { published }) => {
        if (published) {
            customToast.success('이야기를 발행했어요. 페이지의 "이야기 목록" 모듈에 보여요.');
            setPostId(null);
            return;
        }
        customToast.success('저장했어요.');
        if (postId === 'new') {
            setPostId(saved.id);
        } else {
            postQuery.refetch();
        }
    }

    const handleDelete = (post) => {
        if (!window.confirm(`"${post.title}" 이야기를 삭제할까요? 되돌릴 수 없어요.`)) {
            return;
        }
        deletePostMutation.mutate({ id: post.id });
    }

    // 내 페이지 맨 위에 이 이야기로 가는 링크 버튼을 넣는다 (설문과 같음, 일반 링크 모듈이라 페이지 관리에서 옮기거나 지울 수 있다)
    const addToPage = async (post) => {
        if (!window.confirm(`내 페이지 맨 위에 "${post.title}" 링크 버튼을 넣을까요?`)) {
            return;
        }
        CustomBackdropController().showBackdrop();
        try {
            await LinkItemsApi().create({
                body: { type: 'LINK', userPageId: userPage.id, title: post.title, linkUrl: `https://shool.uk/${userPage.domainName}/p/${post.id}`, description: '', thumbnailUrl: post.thumbnailUrl || '', linkCategoryId: '', openInApp: false },
            });
            customToast.success('내 페이지에 이야기 버튼을 넣었어요. 페이지 관리에서 위치를 바꿀 수 있어요.');
        } catch (error) {
            customToast.error(error?.response?.data?.message || '넣지 못했어요. 다시 시도해 주세요.');
        } finally {
            CustomBackdropController().hideBackdrop();
        }
    }

    const goAddModule = () => queryParamsHook.setSearchParams({ tabType: 'PAGE_MANAGE' }, { replace: true });

    if (!userPage?.id) {
        return null;
    }

    if (postId) {
        if (postId !== 'new' && postQuery.isLoading) {
            return <div className="h-96 animate-pulse rounded-[15px] bg-white" aria-busy="true" />;
        }
        if (postId !== 'new' && !postQuery.data) {
            return (
                <div className="rounded-[15px] bg-white p-8 text-center">
                    <p className="text-sm font-semibold">이야기를 찾을 수 없어요.</p>
                    <button type="button" onClick={() => setPostId(null)} className="mt-3 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white">이야기 목록으로</button>
                </div>
            );
        }
        return (
            <FgPostEditor
                key={postId === 'new' ? 'new' : postQuery.data.id}
                userPageId={userPage.id}
                domainName={userPage.domainName}
                post={postId === 'new' ? null : postQuery.data}
                groups={groups}
                onBack={() => setPostId(null)}
                onSaved={handleSaved}
            />
        );
    }

    return (
        <div className="flex flex-col gap-5">
            <section className="flex flex-col gap-4 rounded-[15px] border border-gray-100 bg-white p-5 shadow-[var(--defaultBoxShadow)] min-[993px]:p-7">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-bold">이야기 <span className="text-base font-semibold text-[var(--mainColor)]">{total}</span></h2>
                        <p className="mt-1 text-sm text-gray-500 break-keep">일기·소설·시·자기소개처럼 긴 이야기를 쓰면 이야기마다 <b className="text-gray-700">shool.uk/{userPage.domainName}/p/...</b> 주소가 생기고, 검색에도 걸려요.</p>
                    </div>
                    <button type="button" onClick={() => setPostId('new')} className="shrink-0 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-gray-700">
                        ＋ 새 이야기 쓰기
                    </button>
                </div>

                <div className="flex flex-col gap-2 rounded-xl bg-[var(--mainColor100)] px-4 py-3 min-[600px]:flex-row min-[600px]:items-center">
                    <p className="flex-1 text-[13px] text-gray-700 break-keep">
                        발행한 이야기는 페이지에 <b>이야기 목록 모듈</b>을 넣어야 보여요. 그룹(예: 일기, 소설)을 나눠 두면 모듈마다 다른 이야기를 모아 보여 줄 수 있어요.
                    </p>
                    <button type="button" onClick={goAddModule} className="shrink-0 self-start rounded-lg bg-white px-3 py-2 text-xs font-bold text-gray-800 ring-1 ring-black/[0.06] hover:bg-gray-50 min-[600px]:self-auto">
                        모듈 추가하러 가기 →
                    </button>
                </div>

                {postsQuery.isLoading &&
                    <ul className="flex flex-col gap-2" aria-busy="true">
                        {[0, 1, 2].map(i => <li key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
                    </ul>
                }

                {!postsQuery.isLoading && posts.length === 0 &&
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <p className="text-sm font-semibold">아직 쓴 이야기가 없어요</p>
                        <p className="text-xs text-gray-500">첫 이야기를 써 보세요. 임시저장해 두고 나중에 발행해도 돼요.</p>
                    </div>
                }

                {posts.length > 0 &&
                    <ul className="flex flex-col divide-y divide-gray-100">
                        {posts.map(post => (
                            <li key={post.id} className="flex gap-3 py-3.5">
                                <button type="button" onClick={() => setPostId(post.id)} className="flex min-w-0 flex-1 gap-3 text-left">
                                    {post.thumbnailUrl
                                        ? <img src={post.thumbnailUrl} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-black/5" />
                                        : <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-xl text-gray-300 ring-1 ring-black/5" aria-hidden="true">✎</span>
                                    }
                                    <span className="min-w-0 flex-1">
                                        <span className="flex flex-wrap items-center gap-1.5">
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${post.status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {post.status === 'PUBLISHED' ? '발행' : '임시저장'}
                                            </span>
                                            {post.group && <span className="rounded-full bg-[var(--mainColor100)] px-2 py-0.5 text-[11px] font-semibold text-[var(--mainColor)]">{post.group}</span>}
                                            {post.visibility === 'PASSWORD' && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">🔒 비밀번호</span>}
                                            {post.visibility === 'PRIVATE' && <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[11px] font-semibold text-white">나만 보기</span>}
                                        </span>
                                        <span className="mt-1 block truncate text-[15px] font-bold text-gray-900">{post.title}</span>
                                        <span className="block truncate text-xs text-gray-500">{post.excerpt || '본문 없음'}</span>
                                    </span>
                                </button>
                                <div className="flex shrink-0 flex-col items-end justify-between gap-1">
                                    <span className="text-[11px] text-gray-400">
                                        {post.status === 'PUBLISHED' ? moment(post.publishedAt).format('YYYY.MM.DD HH:mm') : `${moment(post.updatedAt).format('MM.DD HH:mm')} 수정`}
                                    </span>
                                    <div className="flex">
                                        {post.status === 'PUBLISHED' && post.visibility !== 'PRIVATE' &&
                                            <>
                                                <button type="button" onClick={() => addToPage(post)} className={ICON_BUTTON}>페이지에 넣기</button>
                                                <a href={`https://shool.uk/${userPage.domainName}/p/${post.id}`} target="_blank" rel="noreferrer" className={ICON_BUTTON}>보기</a>
                                            </>
                                        }
                                        <button type="button" onClick={() => setPostId(post.id)} className={ICON_BUTTON}>수정</button>
                                        <button type="button" onClick={() => handleDelete(post)} className={`${ICON_BUTTON} hover:text-[var(--redColor1)]`}>삭제</button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                }

                {postsQuery.hasNextPage &&
                    <button
                        type="button"
                        disabled={postsQuery.isFetchingNextPage}
                        onClick={() => postsQuery.fetchNextPage()}
                        className="self-center rounded-full border border-gray-300 px-6 py-2 text-sm font-semibold hover:bg-gray-50"
                    >
                        {postsQuery.isFetchingNextPage ? '불러오는 중...' : '더 보기'}
                    </button>
                }
            </section>
        </div>
    );
}
